import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { AccountIcon, ChartBarIcon, OrderIcon } from '../components/icons';


const LivreurAccountScreen: React.FC = () => {
    const { currentUser, logout, requestNotificationPermission, notificationPermission, updateUserProfile } = useAuth();
    const { wilayas } = useData();
    const [geoPermissionState, setGeoPermissionState] = useState<string>('prompt');
    const [isSavingWilaya, setIsSavingWilaya] = useState(false);
    const [isSavingSchedule, setIsSavingSchedule] = useState(false);
    
    // Local state for schedule to avoid immediate writes to DB
    const [tempSchedule, setTempSchedule] = useState<any>(currentUser?.availabilitySchedule || {});

    // Sync tempSchedule once when currentUser loads
    useEffect(() => {
        if (currentUser?.availabilitySchedule && Object.keys(tempSchedule).length === 0) {
            setTempSchedule(currentUser.availabilitySchedule);
        }
    }, [currentUser?.availabilitySchedule]);


    useEffect(() => {
        if (navigator.permissions) {
            navigator.permissions.query({ name: 'geolocation' as any }).then((result) => {
                setGeoPermissionState(result.state);
                result.onchange = () => setGeoPermissionState(result.state);
            }).catch(() => { /* ignore */ });
        }
    }, []);

    return (
        <div className="min-h-screen bg-gray-50 pb-24">
            <div className="bg-white p-8 pt-12 text-center rounded-b-[3rem] shadow-sm mb-6">
                <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center text-red-600 mx-auto mb-4 border-4 border-white shadow-lg">
                    <span className="text-4xl font-black">{currentUser?.name?.charAt(0)}</span>
                </div>
                <h1 className="text-2xl font-black text-gray-900 tracking-tighter uppercase italic leading-none">{currentUser?.name}</h1>
                <p className="text-[10px] font-black text-gray-900 uppercase tracking-[0.2em] italic mt-1">Livreur Certifié 🛵</p>
                <div className="flex flex-col gap-2 mt-4">
                    <div className="bg-green-100 text-green-700 px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest inline-block mx-auto">
                        Compte Actif
                    </div>
                    {(currentUser?.rating !== undefined || currentUser?.restaurantRating !== undefined || currentUser?.deliveredOrdersCount !== undefined) && (
                        <div className="flex flex-wrap justify-center gap-3 mt-4">
                            {/* Note Clients */}
                            <div className="bg-amber-50 px-3 py-2 rounded-2xl border border-amber-100 flex flex-col items-center">
                                <div className="flex items-center gap-1 text-amber-500 font-black">
                                    <span className="text-sm">⭐</span>
                                    <span>{(currentUser.rating || 0).toFixed(1)}</span>
                                </div>
                                <span className="text-[10px] text-amber-700/60 font-bold uppercase tracking-tighter">Note Clients</span>
                            </div>

                            {/* Note Restaurant */}
                            <div className="bg-blue-50 px-3 py-2 rounded-2xl border border-blue-100 flex flex-col items-center">
                                <div className="flex items-center gap-1 text-blue-600 font-black">
                                    <span className="text-sm">🍴</span>
                                    <span>{(currentUser.restaurantRating || 0).toFixed(1)}</span>
                                </div>
                                <span className="text-[10px] text-blue-700/60 font-bold uppercase tracking-tighter">Note Resto</span>
                            </div>

                            {/* Courses */}
                            <div className="bg-gray-50 px-3 py-2 rounded-2xl border border-gray-100 flex flex-col items-center">
                                <div className="flex items-center gap-1 text-gray-700 font-black">
                                    <span className="text-sm">📦</span>
                                    <span>{currentUser.deliveredOrdersCount || 0}</span>
                                </div>
                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tighter">Courses</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="p-4 space-y-6">
                {/* Section Wilaya - Ma Zone d'Activité */}
                <section className="bg-white rounded-3xl p-6 shadow-sm border border-orange-100 transition-all hover:shadow-md">
                    <h3 className="text-xl font-black text-gray-900 tracking-tighter uppercase italic leading-none mb-1">
                        Ma Zone d'Activité
                    </h3>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.1em] italic mb-4">Wilaya de travail</p>
                    <div className="space-y-4">
                        <p className="text-xs text-gray-500 leading-relaxed">
                            Sélectionnez la Wilaya où vous souhaitez recevoir des missions de livraison.
                        </p>
                        <div className="relative">
                            <select
                                value={currentUser?.wilaya || ''}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (window.confirm(`Voulez-vous changer votre zone d'activité pour ${val} ?`)) {
                                        setIsSavingWilaya(true);
                                        updateUserProfile({ wilaya: val })
                                            .then(() => alert("Wilaya mise à jour !"))
                                            .catch(() => alert("Erreur lors de la mise à jour"))
                                            .finally(() => setIsSavingWilaya(false));
                                    }
                                }}
                                disabled={isSavingWilaya}
                                className="w-full p-4 bg-orange-50 border border-orange-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-orange-500 appearance-none font-bold text-orange-900 disabled:opacity-50"
                            >
                                <option value="" disabled>Choisir ma Wilaya</option>
                                {wilayas.map((w: string) => (
                                    <option key={w} value={w}>{w}</option>
                                ))}
                            </select>
                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-orange-400">
                                ▼
                            </div>
                        </div>
                        {isSavingWilaya && (
                            <p className="text-[10px] text-orange-600 font-bold animate-pulse text-center">Enregistrement en cours...</p>
                        )}
                    </div>
                </section>

                {/* Section Mon Planning - Disponibilités */}
                <section className="bg-white rounded-3xl p-6 shadow-sm border border-purple-100 transition-all hover:shadow-md">
                    <h3 className="text-lg font-black text-gray-900 tracking-tighter uppercase italic mb-4 flex items-center">
                        <span className="bg-purple-100 p-2 rounded-full mr-3 text-xl">📅</span>
                        Mon Planning
                    </h3>
                    <p className="text-xs text-gray-500 leading-relaxed mb-4">
                        Définissez vos heures de disponibilité. Le système vous mettra en ligne automatiquement.
                    </p>

                    <div className="space-y-3">
                        {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'].map((day, index) => {
                            const dayKey = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'][index];
                            const sched = tempSchedule[dayKey] || (currentUser?.availabilitySchedule?.[dayKey]) || { active: false, start: '09:00', end: '22:00' };

                            return (
                                <div key={dayKey} className={`p-3 rounded-2xl border transition-colors ${sched.active ? 'bg-purple-50/50 border-purple-100' : 'bg-gray-50/50 border-gray-100'}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className={`text-sm font-bold ${sched.active ? 'text-purple-900' : 'text-gray-400'}`}>{day}</span>
                                        <div
                                            onClick={() => {
                                                setTempSchedule({
                                                    ...tempSchedule,
                                                    [dayKey]: { ...sched, active: !sched.active }
                                                });
                                            }}
                                            className={`w-10 h-5 rounded-full relative transition-colors cursor-pointer ${sched.active ? 'bg-purple-600' : 'bg-gray-300'}`}
                                        >
                                            <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${sched.active ? 'right-1' : 'left-1'}`}></div>
                                        </div>
                                    </div>

                                    {sched.active && (
                                        <div className="flex items-center gap-2 mt-2">
                                            <div className="flex-1 flex items-center bg-white rounded-lg px-2 py-1 border border-purple-100">
                                                <span className="text-[10px] font-bold text-purple-300 mr-2 uppercase">De</span>
                                                <input
                                                    type="time"
                                                    value={sched.start}
                                                    onChange={(e) => {
                                                        setTempSchedule({
                                                            ...tempSchedule,
                                                            [dayKey]: { ...sched, start: e.target.value }
                                                        });
                                                    }}
                                                    className="w-full text-xs font-bold text-purple-900 bg-transparent focus:outline-none"
                                                />
                                            </div>
                                            <div className="flex-1 flex items-center bg-white rounded-lg px-2 py-1 border border-purple-100">
                                                <span className="text-[10px] font-bold text-purple-300 mr-2 uppercase">À</span>
                                                <input
                                                    type="time"
                                                    value={sched.end}
                                                    onChange={(e) => {
                                                        setTempSchedule({
                                                            ...tempSchedule,
                                                            [dayKey]: { ...sched, end: e.target.value }
                                                        });
                                                    }}
                                                    className="w-full text-xs font-bold text-purple-900 bg-transparent focus:outline-none"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Save Button */}
                        <div className="pt-4">
                            <button
                                onClick={async () => {
                                    setIsSavingSchedule(true);
                                    try {
                                        await updateUserProfile({ availabilitySchedule: tempSchedule });
                                        alert("Planning mis à jour avec succès ! ✅");
                                    } catch (err) {
                                        console.error(err);
                                        alert("Erreur lors de la sauvegarde du planning.");
                                    } finally {
                                        setIsSavingSchedule(false);
                                    }
                                }}
                                disabled={isSavingSchedule}
                                className="w-full py-4 bg-purple-600 text-white font-black rounded-2xl shadow-lg shadow-purple-100 active:scale-95 transition-all disabled:opacity-50"
                            >
                                {isSavingSchedule ? "Sauvegarde..." : "ENREGISTRER MON PLANNING"}
                            </button>
                        </div>
                    </div>
                </section>


                {/* Section Notifications */}
                <section className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-xl font-black text-gray-900 tracking-tighter uppercase italic leading-none mb-1">
                        Sécurité & Alertes
                    </h3>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.1em] italic mb-4">Permissions & notifications</p>
                    <div className="flex items-center justify-between p-4 bg-blue-50/50 rounded-2xl border border-blue-50">
                        <div className="flex-1 mr-4">
                            <p className="font-bold text-gray-800">
                                {notificationPermission === 'granted'
                                    ? "Alertes Activées ✅"
                                    : "Activer les alertes"}
                            </p>
                            <p className="text-[10px] text-gray-500 font-medium leading-relaxed mt-1">
                                {notificationPermission === 'granted'
                                    ? "Vous recevrez des notifications pour vos missions en temps réel."
                                    : "Autorisez les notifications pour recevoir de nouvelles missions instantanément."}
                            </p>
                        </div>
                        {notificationPermission !== 'granted' && (
                            <button
                                onClick={requestNotificationPermission}
                                className="bg-blue-600 text-white px-5 py-2.5 rounded-xl font-bold text-xs shadow-md hover:bg-blue-700 transition-all active:scale-95 whitespace-nowrap"
                            >
                                Autoriser
                            </button>
                        )}
                    </div>
                </section>

                {/* Mode Alarme - Ultra-Compact Notice */}
                <div className="flex items-center gap-3 px-4 py-3 bg-gray-50/50 rounded-2xl border border-gray-100 mb-6">
                    <span className="text-sm opacity-40">⚡</span>
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter flex-1">
                        <span className="text-orange-600/60">Note :</span> Paramètres &gt; Apps &gt; Yumii &gt; Notifications &gt; Alerts &gt; <span className="text-gray-600 underline decoration-gray-200">Ignorer Ne pas déranger.</span>
                    </p>
                    {import.meta.env.PROD && (
                        <button 
                            onClick={() => window.location.href = "app-settings:"}
                            className="text-[8px] font-black text-blue-600 uppercase tracking-widest bg-blue-50 px-2 py-1 rounded-lg border border-blue-100 active:scale-95"
                        >
                            Menu
                        </button>
                    )}
                </div>
                

                {/* Section Géolocalisation */}
                <section className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-xl font-black text-gray-900 tracking-tighter uppercase italic leading-none mb-1">
                        Suivi & GPS
                    </h3>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.1em] italic mb-4">Localisation en temps réel</p>
                    <div className="flex items-center justify-between p-4 bg-green-50/50 rounded-2xl border border-green-50">
                        <div className="flex-1 mr-4">
                            <p className="font-bold text-gray-800">
                                {geoPermissionState === 'granted' ? "Localisation Activée ✅" : geoPermissionState === 'denied' ? "Localisation Bloquée ❌" : "Vérification requise"}
                            </p>
                            <p className="text-[10px] text-gray-500 font-medium leading-relaxed mt-1">
                                {geoPermissionState === 'granted' ? "Votre position est partagée lors de vos missions actives." : "Indispensable pour que le client suive votre progression en temps réel."}
                            </p>
                        </div>
                        {geoPermissionState !== 'granted' && (
                            <button
                                onClick={async () => {
                                    try {
                                        const { Geolocation } = await import('@capacitor/geolocation');
                                        const permission = await Geolocation.requestPermissions();

                                        if (permission.location === 'granted') {
                                            setGeoPermissionState('granted');
                                            alert("Position activée ! Le client pourra suivre votre progression.");
                                        } else if (permission.location === 'denied') {
                                            setGeoPermissionState('denied');
                                            if (navigator.geolocation) {
                                                navigator.geolocation.getCurrentPosition(
                                                    () => { setGeoPermissionState('granted'); alert("Position activée !"); },
                                                    () => alert("Localisation bloquée. Veuillez l'autoriser dans les paramètres de votre navigateur.")
                                                );
                                            } else {
                                                alert("Localisation refusée. Allez dans l'icône cadenas de votre navigateur.");
                                            }
                                        } else if (permission.location === 'prompt' || !permission.location) {
                                            if (navigator.geolocation) {
                                                navigator.geolocation.getCurrentPosition(
                                                    () => { setGeoPermissionState('granted'); alert("Position activée !"); },
                                                    () => { setGeoPermissionState('denied'); alert("Localisation bloquée ou impossible. Vérifiez vos réglages navigateur."); }
                                                );
                                            }
                                        }
                                    } catch (e) {
                                        // Sur Web, on tente getCurrentPosition pour provoquer nativement le prompt Web
                                        if (navigator.geolocation) {
                                            navigator.geolocation.getCurrentPosition(
                                                () => { setGeoPermissionState('granted'); alert("Position activée !"); },
                                                () => { setGeoPermissionState('denied'); alert("Localisation introuvable."); }
                                            );
                                        }
                                    }
                                }}
                                className="bg-green-600 text-white px-5 py-2.5 rounded-xl font-bold text-xs shadow-md hover:bg-green-700 transition-all active:scale-95 whitespace-nowrap"
                            >
                                Autoriser
                            </button>
                        )}
                    </div>
                </section>

                {/* Information Sections */}
                <section className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 space-y-4 mt-6">
                    <h2 className="text-xl font-black text-gray-900 tracking-tighter uppercase italic leading-none mb-4 px-1">Coordonnées</h2>

                    <div className="flex items-center gap-4 group">
                        <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center group-hover:bg-red-50 transition-colors">
                            <span className="text-xl">📧</span>
                        </div>
                        <div className="flex-1 border-b border-gray-50 pb-3">
                            <p className="text-[10px] text-gray-400 font-bold uppercase">Email</p>
                            <p className="text-sm font-medium text-gray-700">{currentUser?.email}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4 group">
                        <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center group-hover:bg-red-50 transition-colors">
                            <span className="text-xl">📱</span>
                        </div>
                        <div className="flex-1">
                            <p className="text-[10px] text-gray-400 font-bold uppercase">Téléphone</p>
                            <p className="text-sm font-medium text-gray-700">{currentUser?.phone || 'Non renseigné'}</p>
                        </div>
                    </div>
                </section>

                <section className="bg-white rounded-3xl p-2 shadow-sm border border-gray-100">
                    <button className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-2xl transition-colors">
                        <div className="flex items-center gap-3 text-gray-700">
                            <span className="text-lg">🛠️</span>
                            <span className="font-bold text-sm text-gray-700">Modifier le profil</span>
                        </div>
                        <span className="text-gray-300">→</span>
                    </button>
                    <button className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-2xl transition-colors">
                        <div className="flex items-center gap-3 text-gray-600">
                            <span className="text-lg">🛡️</span>
                            <span className="font-bold text-sm text-gray-700">Sécurité & MDP</span>
                        </div>
                        <span className="text-gray-300">→</span>
                    </button>
                    <button className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-2xl transition-colors">
                        <div className="flex items-center gap-3 text-gray-600">
                            <span className="text-lg">❓</span>
                            <span className="font-bold text-sm text-gray-700">Aide & Support</span>
                        </div>
                        <span className="text-gray-300">→</span>
                    </button>
                </section>

                <div className="px-2">
                    <button
                        onClick={logout}
                        className="w-full py-4 bg-red-50 text-red-600 font-black rounded-2xl shadow-sm border border-red-100 hover:bg-red-100 transition-all active:scale-95"
                    >
                        SE DÉCONNECTER
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LivreurAccountScreen;
